import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { createLoadingOverlayQueue } from "../lib/slow_visit_overlay"

// Live price quote on the two order pages (lead_orders/new, aged_leads/lead_orders/new):
// fills the read-only Price Per Lead and Total Price boxes from POST /ajax/ppl and
// POST /ajax/lead_order_total. These are QUOTES only; the amount charged is
// re-derived server-side at create (LeadOrdersCommon#normalize_lead_order_total_price).
//
// The two pages differ only in what the values say:
// - aged: quotes carry aged_bucket (the bucket field, else the page's @aged_bucket); with
//   neither, the key is left out, exactly as the old jQuery sent it (its settings merge
//   dropped the undefined value), so the server prices it the same way.
// - loadingOverlay: the realtime page holds the shared #loading-overlay while the
//   on-load total is fetched (lib/slow_visit_overlay's queue: overlapping quotes keep
//   it up until the last one lands; a release after navigation is a no-op).
//
// The body stays a form-encoded string in the same key order the old inline jQuery
// sent (a missing quality tier posts as an empty param), so the server parses the same params.
const FORM_CONTENT_TYPE = "application/x-www-form-urlencoded; charset=UTF-8"
const QUANTITY_DEBOUNCE_MS = 500
const QTY_MSG = "Enter quantity to see total"
const PICK_BOTH_MSG = "Select lead type and quantity to see total"

export default class extends Controller {
  static targets = ["leadClass", "quantity", "pricePerLead", "total", "qualityTier", "agedBucket"]

  static values = {
    leadPriceUrl: String,
    totalPriceUrl: String,
    csrfToken: String,
    aged: Boolean,
    agedBucket: String, // absent when the page has no @aged_bucket
    loadingOverlay: Boolean
  }

  connect() {
    // A Turbo cache preview is replaced by the real page, which quotes on its own connect.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.quoteOnLoad()
  }

  disconnect() {
    clearTimeout(this.quantityTimer)
  }

  // Lead type, quality tier and bucket changes re-quote the price, then the total.
  requote() {
    const leadClass = this.leadClassTarget.value
    if (!leadClass) {
      this.pricePerLeadTarget.value = "Select a lead type to see price"
      this.totalTarget.value = QTY_MSG
      return
    }

    this.quotePrice(leadClass, () => this.quantityChanged())
  }

  // Quantity typing quotes the total once the buyer pauses.
  quantityChanged() {
    clearTimeout(this.quantityTimer)
    this.quantityTimer = setTimeout(() => {
      this.quoteTotal(this.leadClassTarget.value, this.quantity())
    }, QUANTITY_DEBOUNCE_MS)
  }

  // private

  quoteOnLoad() {
    const leadClass = this.leadClassTarget.value
    const quantity = this.quantity()
    if (!leadClass) return

    this.quotePrice(leadClass, () => {
      if (quantity > 0) this.quoteTotal(leadClass, quantity, { holdOverlay: this.loadingOverlayValue })
    })
  }

  async quotePrice(leadClass, onPriced) {
    try {
      const data = await this.postQuote(this.leadPriceUrlValue, [["lead_class", leadClass]])
      if (data.price_per_lead) {
        this.pricePerLeadTarget.value = `$${parseFloat(data.price_per_lead).toFixed(2)} per lead`
        onPriced()
      } else {
        this.pricePerLeadTarget.value = "Price unavailable"
        this.totalTarget.value = QTY_MSG
      }
    } catch (error) {
      this.pricePerLeadTarget.value = "Error fetching price"
      this.totalTarget.value = QTY_MSG
      console.error("Lead price AJAX error:", error.message)
    }
  }

  async quoteTotal(leadClass, quantity, { holdOverlay = false } = {}) {
    if (!leadClass || quantity <= 0) {
      this.totalTarget.value = PICK_BOTH_MSG
      return
    }

    if (holdOverlay) this.overlayQueue().show()
    try {
      const data = await this.postQuote(this.totalPriceUrlValue, [["lead_class", leadClass], ["lead_quantity", quantity]])
      this.totalTarget.value = data.total_cents >= 0 ? `($${(data.total_cents / 100).toFixed(2)})` : "Total unavailable"
    } catch (error) {
      this.totalTarget.value = "Error calculating total"
      console.error("Total price AJAX error:", error.message)
    } finally {
      if (holdOverlay) this.overlayQueue().hide()
    }
  }

  async postQuote(url, params) {
    const entries = [["authenticity_token", this.csrfTokenValue], ...params]
    const agedBucket = this.agedValue ? this.currentAgedBucket() : undefined
    if (agedBucket !== undefined) entries.push(["aged_bucket", agedBucket])
    entries.push(["lead_quality", this.selectedQualityTier()])

    const response = await post(url, {
      body: formEncode(entries),
      contentType: FORM_CONTENT_TYPE,
      responseKind: "json"
    })
    if (!response.ok) throw new Error(`${response.statusCode} ${response.response.statusText}`.trim())
    return response.json
  }

  quantity() {
    return parseInt(this.quantityTarget.value, 10) || 0
  }

  // Read at quote time: the bucket is a hidden field (availability link) or a live select
  // (direct entry); a blank field falls back to the page's @aged_bucket, when it has one.
  currentAgedBucket() {
    const field = this.hasAgedBucketTarget ? this.agedBucketTarget.value : ""
    if (field) return field
    return this.hasAgedBucketValue ? this.agedBucketValue : undefined
  }

  // No tier radios (a fresh realtime form) posts an empty lead_quality; the server quotes premium.
  selectedQualityTier() {
    return this.qualityTierTargets.find((input) => input.checked)?.value ?? ""
  }

  overlayQueue() {
    this.loadingQueue ||= createLoadingOverlayQueue()
    return this.loadingQueue
  }
}

// jQuery.param's encoding: encodeURIComponent with %20 as "+".
function formEncode(entries) {
  const encode = (value) => encodeURIComponent(value ?? "").replace(/%20/g, "+")
  return entries.map(([key, value]) => `${encode(key)}=${encode(value)}`).join("&")
}
